import SecondaryButton from '@components/V3/Utils/SecondaryButton';
import { useSaveUserProfile } from '@actions';
import { SaveMentorProfileData, SaveStudentProfileData } from '@shared/clientTypes/misc';
import AgoraInput from '@utils/AgoraInput';
import React, { useEffect, useState } from 'react';
import { COLORS } from '../../../v3/global';
import './profile-modal-users.styles.scss';
import Input from '@components/V4/Inputs/Input';
import Select from '@components/V4/Select/Select';
import AgoraDatePicker from '@components/V3/Utils/InputsV3/AgoraDatePicker';
import { countries } from 'country-codes-flags-phone-codes';
import { UserRole } from '@shared/enums';
import moment from 'moment';
import { z } from 'zod';
import { phoneNumberValidation } from '@shared/validations/generics';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

type Props = {
  handleCurrentPage: (page: string) => void;
  userId: string;
  userData: any;
  refetchUserData: () => void;
};

const contactSchema = z.object({
  firstName: z.string().min(1, { message: 'First Name is required.' }),
  lastName: z.string().min(1, { message: 'Last Name is required.' }),
  email: z.string().email({ message: 'Invalid email address.' }),
  countryCode: z.string().min(1, 'Country code is required.'),
  phone: phoneNumberValidation,
  startContractDate: z.string().nullable().optional(),
  endContractDate: z.string().nullable().optional(),
});

type FormData = z.infer<typeof contactSchema>;

const EditProfileDetailsPage = ({
  handleCurrentPage,
  userId,
  userData,
  refetchUserData,
}: Props) => {
  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    control,
    formState: { errors, touchedFields },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(contactSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      firstName: userData?.firstName ?? '',
      lastName: userData?.lastName ?? '',
      email: userData?.email ?? '',
      phone: userData?.phoneNumber?.number,
      countryCode: userData?.phoneNumber?.countryCode,
      startContractDate: userData?.startContractDate ?? null,
      endContractDate: userData?.endContractDate ?? null,
    },
  });

  const isStudent = userData?.roles?.includes(UserRole.Student);

  const { field: countryCodeField, fieldState: countryCodeFieldState } = useController({
    name: 'countryCode',
    control,
  });

  const { field: phoneNumberField, fieldState: phoneNumberFieldState } = useController({
    name: 'phone',
    control,
  });
  const { field: startContractDateField } = useController({
    name: 'startContractDate',
    control,
  });
  const { field: endContractDateField } = useController({
    name: 'endContractDate',
    control,
  });

  const touchAllFields = (fields: Partial<Record<keyof FormData, string | null | undefined>>) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const { mutate: saveUserProfile } = useSaveUserProfile(userId, {
    onSuccess: () => {
      refetchUserData();
      handleCurrentPage('user-profile');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (isValid) {
      const userDetailsToUpdate: SaveMentorProfileData | SaveStudentProfileData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: {
          number: data.phone,
          countryCode: data.countryCode?.split('#')?.[0],
        },
        ...(isStudent && {
          startContractDate: data.startContractDate,
          endContractDate: data.endContractDate,
        }),
      };

      saveUserProfile(userDetailsToUpdate);
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  useEffect(() => {
    const formData = getValues();

    touchAllFields(formData);
  }, []);

  return (
    <div className="mt-2">
      <div className="flex flex-col justify-between h-[60vh]">
        <div>
          <h3 className="modal-page-section-title mb-6 text-customGrey">Contact Info</h3>

          <div className="flex flex-row gap-2">
            <Input
              type="firstName"
              autoFocus
              id="firstName"
              placeholder="John Doe"
              label="First Name"
              isTouched={touchedFields.firstName}
              isValid={!errors.firstName}
              errorText={errors.firstName?.message}
              {...register('firstName', { required: true })}
            />

            <Input
              type="lastName"
              autoFocus
              id="lastName"
              placeholder="John Doe"
              label="Last Name"
              isTouched={touchedFields.lastName}
              isValid={!errors.lastName}
              errorText={errors.lastName?.message}
              {...register('lastName', { required: true })}
            />
          </div>

          <Input
            type="email"
            autoFocus
            id="email"
            placeholder="john.doe@gmail.com"
            label="Email"
            isTouched={touchedFields.email}
            isValid={!errors.email}
            errorText={errors.email?.message}
            {...register('email', { required: true })}
          />

          <div className="flex flex-row gap-2">
            <Select
              className="max-w-[33.33%]"
              value={countryCodeField.value}
              onSelect={(value) => countryCodeField.onChange(value)}
              onClear={() => countryCodeField.onChange('')}
              onBlur={countryCodeField.onBlur}
              options={countries.map((country, index) => ({
                label: `(${country.dialCode}) ${country.name}`,
                value: `${country.dialCode}#${index}`,
              }))}
              label="Country"
              size="large"
              isRequired
              isValid={!countryCodeFieldState.error}
              isTouched={countryCodeFieldState.isTouched}
              errorText={countryCodeFieldState.error?.message}
            />

            <Input
              type="tel"
              id="phone"
              label="Phone"
              isRequired
              name="countryCode"
              prefix={countryCodeField.value?.split('#')?.[0]}
              onChange={(value) => phoneNumberField.onChange(value)}
              onBlur={phoneNumberField.onBlur}
              value={phoneNumberField.value}
              isDisabled={!countryCodeField.value}
              isValid={!phoneNumberFieldState.error}
              isTouched={phoneNumberFieldState.isTouched}
              errorText={countryCodeField.value ? phoneNumberFieldState.error?.message : undefined}
            />
          </div>

          {isStudent ? (
            <div className="flex flex-row gap-2 mt-4">
              <AgoraDatePicker
                value={startContractDateField.value ? moment(startContractDateField.value) : undefined}
                onChange={(value) =>
                  startContractDateField.onChange(value ? value.format('YYYY-MM-DD') : null)
                }
                onBlur={startContractDateField.onBlur}
                picker="date"
                size="large"
                label="Start Contract Date"
              />

              <AgoraDatePicker
                value={endContractDateField.value ? moment(endContractDateField.value) : undefined}
                onChange={(value) =>
                  endContractDateField.onChange(value ? value.format('YYYY-MM-DD') : null)
                }
                onBlur={endContractDateField.onBlur}
                picker="date"
                size="large"
                label="End Contract Date"
              />
            </div>
          ) : null}
        </div>

        <SecondaryButton
          onClick={submitHandler}
          backgroundColor={COLORS.WHITE}
          height="32px"
          padding="4px 10px 4px 10px"
          margin="12px 0 0 0"
          width="100%"
        >
          <span className="button-text" style={{ color: COLORS.BLACK }}>
            Save
          </span>
        </SecondaryButton>
      </div>
    </div>
  );
};

export default EditProfileDetailsPage;
